/**
 * Mock data para catálogos de Antecedentes Financieros (Paso 3)
 * Fuente: spec final v1 (2026-04-27)
 */

import {
    AntecedentesCatalogsDto,
    CatalogItemDto,
    ContainerPackageTypeDto,
    PalletPackageTypeDto,
    ColdTreatmentIndicatorDto,
    ControlledAtmosphereDto,
    VentilationUnitDto,
    SpeciesPackageTypeDto,
} from '@opb-workspace/shared';

/** Catálogo mínimo para mocks (códigos alineados al legacy típico). */
export const MOCK_CONTAINER_PACKAGE_TYPES: ContainerPackageTypeDto[] = [
    { containerTypeId: 101, code: 'CONT20' },
    { containerTypeId: 102, code: 'CONT40' },
    { containerTypeId: 103, code: 'CONT40HC' },
    { containerTypeId: 104, code: 'REEFER20' },
    { containerTypeId: 105, code: 'REEFER40' },
    /** Código típico en mocks de booking (apply-booking.mock). */
    { containerTypeId: 108, code: '40RF' },
    { containerTypeId: 106, code: 'OPEN_TOP' },
    { containerTypeId: 107, code: 'FLAT_RACK' },
];

/** `tblTypeOfPackagePrimaryTransportUnits` — filas no-contenedor (ejemplos). */
export const MOCK_PALLET_PACKAGE_TYPES: PalletPackageTypeDto[] = [
    { palletTypeId: 201, code: 'EUR PALLET' },
    { palletTypeId: 202, code: 'ESTANDAR' },
    { palletTypeId: 203, code: 'CHEP' },
];

/** Misma fuente que `tblParameterValues` ParameterId = 22 (OPBSDEV). */
export const MOCK_VENTILATION_UNITS: VentilationUnitDto[] = [
    { ventilationUnitId: 1, code: '%' },
    { ventilationUnitId: 2, code: 'CBM' },
];

/** `tblTypeOfPackages` OPBSDEV — Code = NamePlural. */
export const MOCK_SPECIES_PACKAGE_TYPES: SpeciesPackageTypeDto[] = [
    { typeOfPackageId: 1, code: 'BAGS' },
    { typeOfPackageId: 2, code: 'CASES' },
    { typeOfPackageId: 3, code: 'BOXES' },
    { typeOfPackageId: 4, code: 'DRUMS' },
    { typeOfPackageId: 5, code: 'TRAYS' },
    { typeOfPackageId: 6, code: 'BINS' },
    { typeOfPackageId: 7, code: 'PALLETS' },
    { typeOfPackageId: 8, code: 'CARTONS' },
    { typeOfPackageId: 9, code: 'BLOCKS' },
    { typeOfPackageId: 10, code: 'BULKS' },
    { typeOfPackageId: 11, code: 'NETS' },
    { typeOfPackageId: 12, code: 'SACKS' },
    { typeOfPackageId: 13, code: 'PACKAGES' },
    { typeOfPackageId: 14, code: 'SLIPSHEETS' },
    { typeOfPackageId: 15, code: 'ROLLS' },
    { typeOfPackageId: 16, code: 'RACKS' },
    { typeOfPackageId: 17, code: 'BUNDLES' },
    { typeOfPackageId: 21, code: 'RAISIN' },
];

/** Misma fuente que `tblParameterValues` ParameterId = 62 (OPBSDEV). */
export const MOCK_COLD_TREATMENT_INDICATORS: ColdTreatmentIndicatorDto[] = [
    { coldTreatmentIndicatorId: 1, name: 'Con tratamiento' },
    { coldTreatmentIndicatorId: 2, name: 'Sin tratamiento' },
];

/** Alineado a `tblControlledAtmospheres` OPBSDEV (Ids estables en backup típico). */
export const MOCK_CONTROLLED_ATMOSPHERES: ControlledAtmosphereDto[] = [
    { controlledAtmosphereId: 1, name: 'Afam' },
    { controlledAtmosphereId: 3, name: 'Daikin' },
    { controlledAtmosphereId: 4, name: 'EverFresh' },
    { controlledAtmosphereId: 2, name: 'Extrafresh' },
    { controlledAtmosphereId: 5, name: 'Liventus' },
    { controlledAtmosphereId: 6, name: 'Maxtend' },
    { controlledAtmosphereId: 7, name: 'Nitec' },
    { controlledAtmosphereId: 8, name: 'PurFresh' },
    { controlledAtmosphereId: 9, name: 'Seacare' },
    { controlledAtmosphereId: 10, name: 'Smartcare' },
    { controlledAtmosphereId: 11, name: 'Star Fresh' },
    { controlledAtmosphereId: 12, name: 'Starcare' },
    { controlledAtmosphereId: 13, name: 'Starcool' },
    { controlledAtmosphereId: 14, name: 'TranFresh' },
    { controlledAtmosphereId: 15, name: 'Xtend' },
    { controlledAtmosphereId: 16, name: 'Xtendfresh' },
];

/** Alineado a OPBSDEV.tblCustomsWayOfTransports (orden por nombre, igual que el SP). */
export const MOCK_WAY_OF_TRANSPORTS: CatalogItemDto[] = [
    { id: 2, name: 'AEREO', remarks: undefined },
    { id: 5, name: 'CARRETERO/TERRESTRE', remarks: undefined },
    { id: 4, name: 'FERROVIARIO', remarks: undefined },
    { id: 1, name: 'MARITIMA', remarks: undefined },
    { id: 6, name: 'OLEODUCTOS, GASODUCTOS', remarks: undefined },
    { id: 8, name: 'OTRA', remarks: undefined },
    { id: 3, name: 'POSTAL', remarks: undefined },
    {
        id: 7,
        name: 'TENDIDO ELECTRICO (AEREO,SUBT)',
        remarks: undefined,
    },
];

export const MOCK_CATALOGS: AntecedentesCatalogsDto = {
    clauseOfSales: [
        { id: 1, name: 'FOB', remarks: 'Free on Board' },
        { id: 2, name: 'CIF', remarks: 'Cost, Insurance & Freight' },
        { id: 3, name: 'C&F', remarks: 'Cost & Freight' },
        { id: 4, name: 'DDP', remarks: 'Delivered Duty Paid' },
        { id: 5, name: 'EXW', remarks: 'Ex Works' },
    ],

    typeOfCargoes: [
        { id: 1, name: 'FRIGORIZADOS', remarks: 'Productos refrigerados' },
        { id: 2, name: 'CONGELADOS', remarks: 'Productos congelados' },
        { id: 3, name: 'FRUTAS FRESCAS', remarks: 'Frutas sin procesar' },
        { id: 4, name: 'VINO', remarks: 'Productos viníc olas' },
        { id: 5, name: 'QUÍMICOS', remarks: 'Productos químicos' },
        { id: 6, name: 'GENERAL', remarks: 'Carga general' },
    ],

    methodOfPayments: [
        { id: 1, name: 'COB1', remarks: 'Cobranza a la vista' },
        { id: 2, name: 'COB2', remarks: 'Cobranza 30 días' },
        { id: 3, name: 'PPD', remarks: 'Prepago' },
        { id: 4, name: 'TT', remarks: 'Transferencia Telegráfica' },
        { id: 5, name: 'LC', remarks: 'Carta de Crédito' },
    ],

    modeOfSales: [
        { id: 1, name: 'BAJO COND.', remarks: 'Bajo condición' },
        { id: 2, name: 'CONSIGNACIÓN', remarks: 'Venta en consignación' },
        { id: 3, name: 'CONTADO', remarks: 'Venta al contado' },
        { id: 4, name: 'CRÉDITO', remarks: 'Venta a crédito' },
    ],

    wayOfTransports: MOCK_WAY_OF_TRANSPORTS,

    fobCurrencies: [
        { id: 1, name: 'USD', remarks: undefined },
        { id: 2, name: 'EUR', remarks: undefined },
    ],

    freightConditions: [
        { id: 1, name: 'Collect', remarks: 'Collect' },
        { id: 2, name: 'Full Collect', remarks: 'Full Collect' },
        { id: 3, name: 'Prepaid', remarks: 'Prepaid' },
        { id: 4, name: 'Full Prepaid', remarks: 'Full Prepaid' },
    ],
};
